import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { debouncedSearchLatest } from '@vsolv/utils/rxjs-operators';
import { ThemeColor } from '@vsolv/vectors-ui/theming';
import { BehaviorSubject, Observable, ReplaySubject, merge, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { PropertyService } from '../../services';

@Component({
  selector: 'vs-property-set-picker',
  templateUrl: './property-set-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PropertySetPickerComponent,
    },
  ],
})
export class PropertySetPickerComponent implements ControlValueAccessor, OnDestroy {
  constructor(
    public elementRef: ElementRef,
    private propertySvc: PropertyService
  ) {}

  @Output() openChanged = new EventEmitter<boolean>();
  @Output() valueChanges = new EventEmitter<PropertySet.Model | null>();

  @PropertyListener('value') value$ = new ReplaySubject<PropertySet.Model>(1);
  @Input() value: PropertySet.Model | null = null;

  readonly searchQuery$ = new BehaviorSubject<string>('');
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loadingSubject.asObservable();

  private readonly searchTriggers$ = merge(this.searchQuery$, this.value$.pipe(map(value => value?.name ?? '')));

  overlayOpen = false;
  readonly menuMaxHeight = 'clamp(128px, 25vh, 320px)';

  propertySets$: Observable<PropertySet.Model[]> = this.searchTriggers$.pipe(
    debouncedSearchLatest((search: string) => {
      this.loadingSubject.next(true);
      return this.propertySvc.listPropertySets$({ search }).pipe(
        map(propertySets => propertySets.items ?? []),
        withLatestFrom(this.value$.pipe(startWith(this.value ?? null))),
        switchMap(([propertySets, selected]) => {
          if (selected && !propertySets.find(propertySet => propertySet.id === selected.id)) {
            return this.propertySvc
              .listPropertySets$({ search: selected.name })
              .pipe(map(additional => [additional.items?.[0], ...propertySets].filter(Boolean) as PropertySet.Model[]));
          }

          return of(propertySets);
        }),
        tap(() => this.loadingSubject.next(false)),
        catchError(() => {
          this.loadingSubject.next(false);
          return of([] as PropertySet.Model[]);
        })
      );
    })
  );

  @Input() placeholder = 'Find a Property Set...';
  @Input() required = false;
  @Input() disabled = false;
  @Input() themeColor: ThemeColor = 'primary';
  touched = false;

  @Input() compareWith: (v0: PropertySet.Model | null, v1: PropertySet.Model | null) => boolean = (
    v0: PropertySet.Model | null,
    v1: PropertySet.Model | null
  ) => {
    switch (typeof v0) {
      case 'object':
        return JSON.stringify(v0) === JSON.stringify(v1);
      default:
        return v0 === v1;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: PropertySet.Model | null) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  selectValueChanged(value: PropertySet.Model) {
    this.markAsTouched();
    this.value?.id === value?.id ? (this.value = null) : (this.value = value);
    this.close();
    this.onChange(this.value);
    this.valueChanges.next(this.value);
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.overlayOpen = !this.overlayOpen;
  }

  writeValue(value: PropertySet.Model | null): void {
    this.value = value;
  }
  registerOnChange(onChange: (_value: PropertySet.Model | null) => {}): void {
    this.onChange = onChange;
  }
  registerOnTouched(onTouched: () => {}): void {
    this.onTouched = onTouched;
  }
  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  ngOnDestroy(): void {
    this.value$.complete();
    this.searchQuery$.complete();
    this.loadingSubject.complete();
  }
}
